import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getCoreRowModel } from '@tanstack/react-table';

import { Box, Paper, Typography } from '@mui/material';

import { Storage } from '../../../../utils/Storage';
import useConfirmDialog from '../../../../hooks/useConfirmDialog';
import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';
import { getRoles, exportRoles, deleteRole } from '../../../../services/role.service';

import { FilterableTypes } from '../../../../types/FilterableTypes';
import { CONFIRM_DIALOG_ROLE_DELETE, SearchBarFilterTypes } from '../../../../constants/Constants';

import { Table, useStandardTable as useTable } from '../../../common/table';
import SearchBar from '../../../common/SearchBar/SearchBar';
import { Role } from '../../../../types/Role';

import RoleForm from './roleForm';
import { makeRoleColumn } from './makeRoleColumn';

export default function Roles() {
  const navigateTo = useNavigate();
  const { notifyMessage } = useNotifyMessage();

  const [paramFilter, setParamFilter] = useState<any>({});
  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });

  const manageRolePolicies = (id: string) => navigateTo(`/roles/${id}`);

  const intialDefaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.ROLES);
    if (filterState) {
      setParamFilter({
        filters: filterState.filters,
      });
    }
  };

  useEffect(() => {
    intialDefaultFilters();
  }, []);

  const columns = useMemo(() => makeRoleColumn(manageRolePolicies, (role: Role) => handleDeleteRole(role)), []);

  const table = useTable<Role>({
    columns,
    accessKey: 'id',
    fetchAsync: getRoles,
    exportAsync: exportRoles,
    params: paramFilter,
    serverSide: true,
  });

  const onSearchChange = (filters: any) => {
    setParamFilter({
      filters: filters.map((v) => {
        v.filterType = FilterableTypes.Contains;
        return v;
      }),
    });
  };

  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_ROLE_DELETE);

  const handleDeleteRole = useCallback(
    async (role: Role) => {
      const { accepted } = await confirm({}); // Open confirmation dialog
      if (!accepted) return;

      // If confirmed, try deleting role
      try {
        await deleteRole(role.id);
        notifyMessage('Deleted role', 'success');
        forceReload();
      } catch (err: any) {
        notifyMessage(err.response.data ?? 'Failed to delete role', 'error');
      }
    },
    [confirm],
  );

  return (
    <>
      {renderDialog()}

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          rowGap: 1,
          columnGap: 1.5,
          mb: 1.5,
          px: { xs: 0.5, sm: 0.75 },
        }}
      >
        <Typography variant='h5' sx={{ m: 0, fontWeight: 700, color: '#1f2d63' }}>
          Roles
        </Typography>
        <Box>
          <RoleForm />
        </Box>
      </Box>
      <React.Fragment>
        <SearchBar pageKey={SearchBarFilterTypes.ROLES} onSearch={onSearchChange} columns={columns} allowSearchAll={true} />
        <Paper>
          <Table {...table} showSearchBar={false} showColumnToggler={false} showAction={true} getCoreRowModel={getCoreRowModel()} />
        </Paper>
      </React.Fragment>
    </>
  );
}
